import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, Music, Instagram, Globe, Heart, DollarSign, X, 
  Youtube, Play, Calendar, MapPin, Users, Award, Mail, Phone,
  ExternalLink, Download, Share2, Image, Video, Disc, Clock,
  Facebook, Twitter
} from 'lucide-react';
import TrackCard from '@/components/TrackCard';
import FollowButton from '@/components/FollowButton';
import ShareButton from '@/components/ShareButton';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = "/api";

// Payment platform icons
const VenmoIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zm-2.8 4.8c.2.3.3.7.3 1.2 0 1.5-.7 3.7-1.3 5.1-1.2 2.8-2.9 4.2-5 4.2-2.3 0-4-1.8-4-4 0-.1 0-.2 0-.3l.8-5.2c.1-.4.4-.7.8-.7h2c.5 0 .8.4.7.9l-.5 3.5c-.1.5.2.9.7.9h.1c1.3 0 2.3-1.5 2.3-3.1 0-.9-.3-1.5-.8-1.9-.5-.4-1.2-.6-2-.6-1.9 0-3.5 1.3-4 3.1-.1.3-.1.6-.1.9 0 1.4.8 2.4 2.1 2.8-.1.6-.3 1.2-.5 1.7-1.9-.5-3.2-2.1-3.2-4.2 0-3.1 2.4-5.7 5.7-5.7 1.4 0 2.6.4 3.4 1.1.7.6 1.2 1.4 1.3 2.3z"/>
  </svg>
);

const CashAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M23.59 3.47A5.1 5.1 0 0020.12.01C17.89-.13 14.26 0 12 0c-2.26 0-5.89-.13-8.12.01A5.1 5.1 0 00.41 3.47c-.27 2.23-.41 5.86-.41 8.53s.14 6.3.41 8.53a5.1 5.1 0 003.47 3.46c2.23.27 5.86.41 8.12.41s5.89.14 8.12-.41a5.1 5.1 0 003.47-3.46c.27-2.23.41-5.86.41-8.53s-.14-6.3-.41-8.53zM17.1 13.98c-.21 1.74-1.64 3.07-3.37 3.29-1.68.21-3.24-.34-4.32-1.64l1.55-1.55c.71.77 1.61 1.11 2.68.89.74-.15 1.33-.67 1.5-1.41.18-.74-.05-1.43-.6-1.87-.38-.3-.84-.5-1.31-.65-.63-.2-1.3-.4-1.88-.75-1.16-.68-1.78-1.71-1.78-2.98 0-1.26.63-2.33 1.73-2.95.86-.48 1.83-.57 2.82-.37.87.17 1.59.58 2.21 1.21l-1.45 1.45c-.44-.47-1.01-.76-1.72-.8-.78-.05-1.47.38-1.72 1.08-.2.55-.04 1.08.41 1.46.5.43 1.14.62 1.77.82.65.21 1.31.42 1.9.76 1.3.74 1.91 1.91 1.68 3.51z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const SoundCloudIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c.014.057.045.094.09.094s.089-.037.099-.094l.19-1.308-.19-1.334c-.01-.057-.044-.09-.078-.09zm1.83-1.229c-.061 0-.12.045-.12.104l-.21 2.563.225 2.458c0 .06.045.104.106.104.061 0 .12-.044.12-.104l.24-2.458-.24-2.563c0-.06-.059-.104-.121-.104zm.945-.089c-.075 0-.135.06-.15.135l-.193 2.64.21 2.544c.016.077.075.138.149.138.075 0 .135-.061.15-.138l.225-2.544-.225-2.64c-.015-.075-.06-.135-.166-.135zm.899-.166c-.09 0-.165.075-.165.165l-.195 2.79.21 2.565c0 .09.075.165.165.165.091 0 .165-.075.165-.165l.225-2.565-.225-2.79c0-.09-.074-.165-.18-.165zm1.035-.45c-.105 0-.195.09-.195.195l-.18 3.24.195 2.595c0 .105.09.195.195.195.104 0 .195-.09.195-.195l.21-2.595-.225-3.24c0-.105-.09-.195-.195-.195zm.93-.39c-.12 0-.21.09-.225.21l-.165 3.63.18 2.595c.016.12.105.21.225.21.12 0 .21-.09.21-.21l.195-2.595-.195-3.63c0-.12-.09-.21-.225-.21zm1.065-.135c-.135 0-.24.105-.24.24l-.15 3.765.165 2.58c0 .135.105.24.24.24.135 0 .24-.105.24-.24l.18-2.58-.18-3.765c0-.135-.105-.24-.24-.24zm.975-.165c-.15 0-.27.12-.27.27l-.135 3.93.15 2.565c0 .15.12.27.27.27.15 0 .27-.12.27-.27l.165-2.565-.18-3.93c0-.15-.12-.27-.27-.27zm1.05-.225c-.165 0-.3.135-.3.3l-.12 4.155.135 2.535c0 .165.135.3.3.3.165 0 .3-.135.3-.3l.15-2.535-.165-4.155c0-.165-.135-.3-.3-.3zm1.8-.75c-.09 0-.165.06-.21.135-.015.015-.015.045-.015.06l-.12 4.935.12 2.475c0 .015 0 .045.015.06.045.075.12.12.21.12.074 0 .149-.045.194-.12.015-.015.015-.045.015-.06l.135-2.475-.135-4.935c0-.015 0-.045-.015-.06-.045-.075-.12-.135-.21-.135zm.99-.135c-.195 0-.345.15-.345.345l-.105 5.07.105 2.43c0 .195.15.345.345.345.195 0 .345-.15.345-.345l.12-2.43-.12-5.07c0-.195-.15-.345-.345-.345zm.99-.12c-.21 0-.375.165-.375.375l-.09 5.19.105 2.415c0 .21.165.375.375.375.21 0 .375-.165.375-.375l.105-2.415-.12-5.19c0-.21-.165-.375-.375-.375zm2.145 5.595c-.225-.045-2.1-.06-2.4.015-.21.045-.375.21-.375.42l-.09 5.19.09 2.355c0 .225.165.39.375.39.15 0 .27-.075.345-.195.03-.045.045-.105.045-.165l.105-2.385-.105-5.19c0-.045-.015-.09-.03-.135l1.635.03c.21 0 .375-.165.375-.375 0-.21-.165-.375-.375-.375zm-.165-1.32c-.27 0-.495.225-.495.495l-.075 5.31.09 2.31c0 .27.225.495.495.495.27 0 .495-.225.495-.495l.09-2.31-.105-5.31c0-.27-.225-.495-.495-.495zm1.095 0c-.285 0-.51.24-.525.525l-.06 5.28.075 2.28c.015.285.24.51.525.51.285 0 .51-.225.525-.51l.09-2.28-.105-5.28c-.015-.285-.24-.525-.525-.525zm1.065.03c-.3 0-.54.24-.54.54v5.25l.075 2.235c0 .3.24.54.54.54.3 0 .54-.24.54-.54l.09-2.25-.09-5.235c0-.3-.24-.54-.54-.54zm3.69-.06c-.255-.015-.495-.015-.75-.015h-2.1c-.315 0-.57.255-.57.57l-.06 5.22.075 2.175c0 .315.255.57.57.57h2.1c.255 0 .495 0 .75-.015 2.1-.135 3.765-1.89 3.765-4.035 0-2.145-1.665-3.885-3.78-4.02z"/>
  </svg>
);

const ArtistProfile = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [artist, setArtist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTipJar, setShowTipJar] = useState(false);
  const [activeTab, setActiveTab] = useState('music');
  const [epkCopied, setEpkCopied] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    booker_name: '', booker_email: '', event_date: '', event_venue: '', event_description: '', agreed_amount: ''
  });

  useEffect(() => {
    fetchArtist();
  }, [id]);

  const fetchArtist = async () => {
    try {
      const [artistRes, tracksRes, showsRes] = await Promise.all([
        axios.get(`${API}/artists/${id}`),
        axios.get(`${API}/tracks?artist_id=${id}`),
        axios.get(`${API}/artists/${id}/upcoming-shows`).catch(() => ({ data: [] }))
      ]);
      setArtist(artistRes.data);
      setTracks(tracksRes.data);
      setUpcomingShows(showsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch artist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyEpk = async () => {
    if (!artist) return;
    const epkUrl = window.location.href;
    const message = `${artist.name}${artist.genre ? ` — ${artist.genre}` : ''}\n${artist.hometown ? `Based in ${artist.hometown}\n` : ''}${artist.bio ? `\n${artist.bio.substring(0, 200)}${artist.bio.length > 200 ? '...' : ''}\n` : ''}\nView full Electronic Press Kit:\n${epkUrl}`;
    try {
      await navigator.clipboard.writeText(message);
      setEpkCopied(true);
      setTimeout(() => setEpkCopied(false), 3000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setEpkCopied(true);
      setTimeout(() => setEpkCopied(false), 3000);
    }
  };

  const handleBookArtist = async () => {
    if (!bookingForm.booker_name || !bookingForm.booker_email || !bookingForm.event_date || !bookingForm.event_venue || !bookingForm.agreed_amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    setBookingLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/checkout`, {
        artist_id: artist.id,
        artist_name: artist.name,
        booker_name: bookingForm.booker_name,
        booker_email: bookingForm.booker_email,
        event_date: bookingForm.event_date,
        event_venue: bookingForm.event_venue,
        event_description: bookingForm.event_description,
        agreed_amount: parseFloat(bookingForm.agreed_amount),
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start booking checkout');
    } finally {
      setBookingLoading(false);
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram': return Instagram;
      case 'twitter': return Twitter;
      case 'facebook': return Facebook;
      case 'tiktok': return () => <TikTokIcon />;
      default: return Globe;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-80 w-full" />
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="aspect-video" />
          ))}
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="text-center py-16">
        <h2 className="font-heading text-2xl font-bold mb-4">Artist not found</h2>
        <Link to="/">
          <Button variant="outline">Back to Discovery</Button>
        </Link>
      </div>
    );
  }

  const hasSocialLinks = artist.social_links && Object.keys(artist.social_links).length > 0;
  const hasStreamingLinks = artist.spotify_url || artist.soundcloud_url || artist.bandcamp_url || artist.youtube_channel;
  const hasContactInfo = user && (artist.contact_email || artist.contact_phone || artist.booking_contact);
  const hasPressPhotos = artist.press_photos && artist.press_photos.length > 0;
  const hasVideoLinks = artist.video_links && artist.video_links.length > 0;
  const hasMusicLinks = artist.music_links && artist.music_links.length > 0;

  return (
    <div className="space-y-8 pb-20" data-testid="artist-profile-page">
      {/* Back button */}
      <Link to="/">
        <Button variant="ghost" size="sm" className="gap-2" data-testid="back-btn">
          <ArrowLeft size={16} />
          Back
        </Button>
      </Link>

      {/* EPK Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-2xl overflow-hidden bg-gradient-to-b from-secondary/20 to-background border border-border"
      >
        {/* Banner Image */}
        <div className="aspect-[21/9] md:aspect-[3/1] relative">
          <img
            src={artist.banner_url || artist.image_url || 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=1200'}
            alt={artist.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=1200'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>
        
        {/* Artist Info Overlay */}
        <div className="relative px-6 pb-6 -mt-24 md:-mt-32">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Profile Image */}
            <div className="w-32 h-32 md:w-48 md:h-48 rounded-2xl overflow-hidden border-4 border-background shadow-2xl flex-shrink-0">
              <img
                src={artist.image_url || 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=400'}
                alt={artist.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=400'; }}
              />
            </div>
            
            {/* Artist Details */}
            <div className="flex-1 space-y-4">
              <div>
                <p className="text-xs text-secondary tracking-widest uppercase mb-1">Twin Cities Artist</p>
                <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight" data-testid="artist-name">
                  {artist.name}
                </h1>
                
                <div className="flex flex-wrap items-center gap-3 mt-3 text-muted-foreground text-sm">
                  {artist.genre && (
                    <Badge variant="secondary" className="gap-1">
                      <Music size={12} />
                      {artist.genre}
                    </Badge>
                  )}
                  {artist.hometown && (
                    <Badge variant="outline" className="gap-1">
                      <MapPin size={12} />
                      {artist.hometown}
                    </Badge>
                  )}
                  {artist.years_active && (
                    <Badge variant="outline" className="gap-1">
                      <Clock size={12} />
                      {artist.years_active} years
                    </Badge>
                  )}
                  {artist.members && (
                    <Badge variant="outline" className="gap-1">
                      <Users size={12} />
                      {artist.members.split(',').length} members
                    </Badge>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <FollowButton 
                  entityType="artist" 
                  entityId={artist.id} 
                  entityName={artist.name}
                  size="default"
                />
                
                {artist.tip_jar && Object.keys(artist.tip_jar).length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setShowTipJar(true)}
                    className="gap-2 border-green-500/50 text-green-400 hover:bg-green-500/10"
                    data-testid="tip-jar-btn"
                  >
                    <DollarSign size={18} />
                    Support Artist
                  </Button>
                )}
                
                <ShareButton
                  title={`${artist.name} - Electronic Press Kit`}
                  description={artist.bio ? artist.bio.substring(0, 150) + '...' : `Check out ${artist.name} - ${artist.genre || 'Twin Cities artist'}`}
                  url={window.location.href}
                />

                <Button
                  variant={epkCopied ? "default" : "outline"}
                  size="sm"
                  onClick={handleCopyEpk}
                  className={`gap-2 transition-all duration-300 ${epkCopied ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'border-secondary/50 text-secondary hover:bg-secondary/10'}`}
                  data-testid="copy-epk-btn"
                >
                  {epkCopied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      EPK Copied!
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Copy EPK Link
                    </>
                  )}
                </Button>

                {artist.press_kit_url && (
                  <Button variant="outline" size="sm" className="gap-2" asChild>
                    <a href={artist.press_kit_url} target="_blank" rel="noopener noreferrer">
                      <Download size={16} />
                      Press Kit
                    </a>
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBooking(true)}
                  className="gap-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  data-testid="book-artist-btn"
                >
                  <Calendar size={16} />
                  Book This Artist
                </Button>
              </div>

              {/* Streaming & Social Links */}
              <div className="flex flex-wrap gap-2">
                {artist.spotify_url && (
                  <Button variant="ghost" size="sm" className="gap-2 text-green-400 hover:text-green-300" asChild>
                    <a href={artist.spotify_url} target="_blank" rel="noopener noreferrer">
                      <SpotifyIcon /> Spotify
                    </a>
                  </Button>
                )}
                {artist.soundcloud_url && (
                  <Button variant="ghost" size="sm" className="gap-2 text-orange-400 hover:text-orange-300" asChild>
                    <a href={artist.soundcloud_url} target="_blank" rel="noopener noreferrer">
                      <SoundCloudIcon /> SoundCloud
                    </a>
                  </Button>
                )}
                {artist.bandcamp_url && (
                  <Button variant="ghost" size="sm" className="gap-2 text-cyan-400 hover:text-cyan-300" asChild>
                    <a href={artist.bandcamp_url} target="_blank" rel="noopener noreferrer">
                      <Disc size={16} /> Bandcamp
                    </a>
                  </Button>
                )}
                {artist.youtube_channel && (
                  <Button variant="ghost" size="sm" className="gap-2 text-red-400 hover:text-red-300" asChild>
                    <a href={artist.youtube_channel} target="_blank" rel="noopener noreferrer">
                      <Youtube size={16} /> YouTube
                    </a>
                  </Button>
                )}
                {artist.website && (
                  <Button variant="ghost" size="sm" className="gap-2" asChild>
                    <a href={artist.website} target="_blank" rel="noopener noreferrer">
                      <Globe size={16} /> Website
                    </a>
                  </Button>
                )}
                {hasSocialLinks && Object.entries(artist.social_links || {}).map(([platform, handle]) => {
                  const Icon = getSocialIcon(platform);
                  return (
                    <Button key={platform} variant="ghost" size="sm" className="gap-2" asChild>
                      <a href={`https://${platform}.com/${handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                        <Icon size={16} /> {platform}
                      </a>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* EPK Bio Excerpt - visible immediately for first impressions */}
      {artist.bio && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="px-1"
        >
          <p className="text-muted-foreground leading-relaxed text-sm md:text-base line-clamp-3" data-testid="artist-bio-excerpt">
            {artist.bio}
          </p>
        </motion.div>
      )}

      {/* EPK Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap bg-background/50 border border-border rounded-lg p-1">
          <TabsTrigger value="music" className="gap-2"><Music size={16} /> Music</TabsTrigger>
          {upcomingShows.length > 0 && (
            <TabsTrigger value="shows" className="gap-2"><Calendar size={16} /> Shows ({upcomingShows.length})</TabsTrigger>
          )}
          <TabsTrigger value="about" className="gap-2"><Users size={16} /> About</TabsTrigger>
          {(hasPressPhotos || artist.press_photo_url) && (
            <TabsTrigger value="photos" className="gap-2"><Image size={16} /> Photos</TabsTrigger>
          )}
          {hasVideoLinks && (
            <TabsTrigger value="videos" className="gap-2"><Video size={16} /> Videos</TabsTrigger>
          )}
          {hasContactInfo && (
            <TabsTrigger value="contact" className="gap-2"><Mail size={16} /> Contact</TabsTrigger>
          )}
        </TabsList>

        {/* Music Tab */}
        <TabsContent value="music" className="mt-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="font-heading text-2xl font-bold mb-6">Music & Tracks</h2>
            
            {tracks.length === 0 && !hasMusicLinks ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Music size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No tracks available yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {tracks.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tracks.map((track) => (
                      <TrackCard key={track.id} track={track} artist={artist} />
                    ))}
                  </div>
                )}
                
                {hasMusicLinks && (
                  <div className="pt-6 border-t border-border">
                    <h3 className="text-lg font-semibold mb-4">External Music Links</h3>
                    <div className="grid gap-3">
                      {(artist.music_links || []).map((link, i) => (
                        <a 
                          key={i} 
                          href={link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border hover:border-secondary/50 transition-colors"
                        >
                          <ExternalLink size={16} className="text-muted-foreground" />
                          <span className="text-sm truncate">{link}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </TabsContent>

        {/* Upcoming Shows Tab */}
        {upcomingShows.length > 0 && (
          <TabsContent value="shows" className="mt-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="font-heading text-2xl font-bold mb-6">Upcoming Shows</h2>
              <div className="space-y-3">
                {upcomingShows.map((show, i) => {
                  const showDate = new Date(show.date + 'T00:00:00');
                  const month = showDate.toLocaleDateString('en-US', { month: 'short' });
                  const day = showDate.getDate();
                  const weekday = showDate.toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <motion.div
                      key={show.id || i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-stretch gap-4 p-4 rounded-xl bg-card/50 border border-border hover:border-secondary/40 transition-colors group"
                      data-testid={`upcoming-show-${i}`}
                    >
                      {/* Date block */}
                      <div className="flex flex-col items-center justify-center min-w-[60px] py-1 px-3 rounded-lg bg-secondary/10 border border-secondary/20">
                        <span className="text-xs text-secondary uppercase tracking-wider font-medium">{month}</span>
                        <span className="text-2xl font-bold leading-none">{day}</span>
                        <span className="text-xs text-muted-foreground">{weekday}</span>
                      </div>
                      {/* Show info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-base truncate group-hover:text-secondary transition-colors">{show.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <MapPin size={14} className="flex-shrink-0" />
                          <span className="truncate">{show.venue}</span>
                        </div>
                        {show.time && (
                          <div className="flex items-center gap-2 mt-0.5 text-sm text-muted-foreground">
                            <Clock size={14} className="flex-shrink-0" />
                            <span>{show.time}</span>
                          </div>
                        )}
                        {show.ticket_url && (
                          <a
                            href={show.ticket_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-secondary hover:text-secondary/80"
                          >
                            <ExternalLink size={12} /> Get Tickets
                          </a>
                        )}
                      </div>
                      {/* Show image thumbnail */}
                      {show.image_url && (
                        <div className="hidden sm:block w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={show.image_url} alt={show.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </TabsContent>
        )}

        {/* About Tab */}
        <TabsContent value="about" className="mt-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
            {/* Bio */}
            {artist.bio && (
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-xl">Biography</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{artist.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Quick Facts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {artist.members && (
                <Card className="bg-card/50 border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                      <Users size={16} /> Members
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{artist.members}</p>
                  </CardContent>
                </Card>
              )}
              
              {artist.hometown && (
                <Card className="bg-card/50 border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                      <MapPin size={16} /> Based In
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{artist.hometown}</p>
                  </CardContent>
                </Card>
              )}
              
              {artist.years_active && (
                <Card className="bg-card/50 border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                      <Clock size={16} /> Years Active
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{artist.years_active} years</p>
                  </CardContent>
                </Card>
              )}
              
              {artist.achievements && (
                <Card className="bg-card/50 border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                      <Award size={16} /> Achievements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{artist.achievements}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="mt-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="font-heading text-2xl font-bold mb-6">Press Photos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {artist.press_photo_url && (
                <a href={artist.press_photo_url} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="aspect-square rounded-lg overflow-hidden border border-border hover:border-secondary/50 transition-colors">
                    <img 
                      src={artist.press_photo_url} 
                      alt={`${artist.name} press photo`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </a>
              )}
              {hasPressPhotos && (artist.press_photos || []).map((photo, i) => (
                <a key={i} href={photo} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="aspect-square rounded-lg overflow-hidden border border-border hover:border-secondary/50 transition-colors">
                    <img 
                      src={photo} 
                      alt={`${artist.name} press photo ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </a>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">Click images to view full size. Right-click to download.</p>
          </motion.div>
        </TabsContent>

        {/* Videos Tab */}
        <TabsContent value="videos" className="mt-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="font-heading text-2xl font-bold mb-6">Videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(artist.video_links || []).map((link, i) => (
                <a 
                  key={i} 
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 rounded-lg bg-card/50 border border-border hover:border-secondary/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Youtube size={24} className="text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Video {i + 1}</p>
                    <p className="text-xs text-muted-foreground truncate">{link}</p>
                  </div>
                  <ExternalLink size={16} className="text-muted-foreground" />
                </a>
              ))}
            </div>
          </motion.div>
        </TabsContent>

        {/* Contact Tab (only visible to logged-in users) */}
        <TabsContent value="contact" className="mt-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-card/50 border-border max-w-lg">
              <CardHeader>
                <CardTitle className="text-xl">Booking & Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {artist.booking_contact && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                      <Calendar size={20} className="text-secondary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Booking</p>
                      <p className="font-medium">{artist.booking_contact}</p>
                    </div>
                  </div>
                )}
                {artist.contact_email && (
                  <a href={`mailto:${artist.contact_email}`} className="flex items-center gap-3 hover:text-secondary transition-colors">
                    <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                      <Mail size={20} className="text-secondary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{artist.contact_email}</p>
                    </div>
                  </a>
                )}
                {artist.contact_phone && (
                  <a href={`tel:${artist.contact_phone}`} className="flex items-center gap-3 hover:text-secondary transition-colors">
                    <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                      <Phone size={20} className="text-secondary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{artist.contact_phone}</p>
                    </div>
                  </a>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Tip Jar Modal */}
      <AnimatePresence>
        {showTipJar && artist.tip_jar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTipJar(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Support {artist.name}</h3>
                    <p className="text-sm text-muted-foreground">Show your appreciation</p>
                  </div>
                </div>
                <button onClick={() => setShowTipJar(false)} className="text-muted-foreground hover:text-foreground">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-3">
                {artist.tip_jar.venmo && (
                  <a
                    href={`https://venmo.com/${artist.tip_jar.venmo.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl bg-[#008CFF]/10 border border-[#008CFF]/30 hover:bg-[#008CFF]/20 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#008CFF] flex items-center justify-center text-white">
                      <VenmoIcon />
                    </div>
                    <div>
                      <p className="font-semibold text-[#008CFF]">Venmo</p>
                      <p className="text-sm text-muted-foreground">{artist.tip_jar.venmo}</p>
                    </div>
                  </a>
                )}

                {artist.tip_jar.cashapp && (
                  <a
                    href={`https://cash.app/${artist.tip_jar.cashapp.replace('$', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl bg-[#00D632]/10 border border-[#00D632]/30 hover:bg-[#00D632]/20 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#00D632] flex items-center justify-center text-white">
                      <CashAppIcon />
                    </div>
                    <div>
                      <p className="font-semibold text-[#00D632]">Cash App</p>
                      <p className="text-sm text-muted-foreground">{artist.tip_jar.cashapp}</p>
                    </div>
                  </a>
                )}

                {artist.tip_jar.paypal && (
                  <a
                    href={`https://paypal.me/${artist.tip_jar.paypal}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl bg-[#0070BA]/10 border border-[#0070BA]/30 hover:bg-[#0070BA]/20 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#0070BA] flex items-center justify-center text-white">
                      <DollarSign size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0070BA]">PayPal</p>
                      <p className="text-sm text-muted-foreground">{artist.tip_jar.paypal}</p>
                    </div>
                  </a>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-6">
                Tips go directly to the artist
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Book {artist?.name}</DialogTitle>
            <DialogDescription>
              A 3% booking fee applies to support Twin Tube and local music.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="b_name" className="text-xs">Your Name *</Label>
                <Input id="b_name" placeholder="Name" value={bookingForm.booker_name} onChange={e => setBookingForm({...bookingForm, booker_name: e.target.value})} data-testid="booking-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="b_email" className="text-xs">Your Email *</Label>
                <Input id="b_email" type="email" placeholder="Email" value={bookingForm.booker_email} onChange={e => setBookingForm({...bookingForm, booker_email: e.target.value})} data-testid="booking-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="b_date" className="text-xs">Event Date *</Label>
                <Input id="b_date" type="date" value={bookingForm.event_date} onChange={e => setBookingForm({...bookingForm, event_date: e.target.value})} data-testid="booking-date" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="b_venue" className="text-xs">Venue *</Label>
                <Input id="b_venue" placeholder="Venue name" value={bookingForm.event_venue} onChange={e => setBookingForm({...bookingForm, event_venue: e.target.value})} data-testid="booking-venue" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="b_desc" className="text-xs">Event Details</Label>
              <Textarea id="b_desc" placeholder="Describe the event..." rows={2} value={bookingForm.event_description} onChange={e => setBookingForm({...bookingForm, event_description: e.target.value})} data-testid="booking-description" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="b_amount" className="text-xs">Agreed Artist Fee ($) *</Label>
              <Input id="b_amount" type="number" min="1" step="0.01" placeholder="500.00" value={bookingForm.agreed_amount} onChange={e => setBookingForm({...bookingForm, agreed_amount: e.target.value})} data-testid="booking-amount" />
            </div>
            {bookingForm.agreed_amount > 0 && (
              <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Artist Fee</span><span>${parseFloat(bookingForm.agreed_amount).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Booking Fee (3%)</span><span className="text-orange-400">${(parseFloat(bookingForm.agreed_amount) * 0.03).toFixed(2)}</span></div>
                <div className="border-t border-border pt-1 flex justify-between font-bold"><span>You Pay Now</span><span className="text-orange-400">${(parseFloat(bookingForm.agreed_amount) * 0.03).toFixed(2)}</span></div>
                <p className="text-[10px] text-muted-foreground mt-1">Artist fee is paid directly to the artist. Only the 3% booking fee is charged now.</p>
              </div>
            )}
            <Button onClick={handleBookArtist} disabled={bookingLoading} className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold gap-2" data-testid="booking-submit">
              {bookingLoading ? 'Processing...' : 'Confirm & Pay Booking Fee'}
              {!bookingLoading && <Calendar size={16} />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistProfile;
